import { useUserStore } from "@/store/user";
import { storeToRefs } from "pinia";
import { computed } from "vue";
import { useRouter } from "vue-router";
import { useAccessDeniedStore } from "@/store/accessDenied";

export const BOKE_ERROR_CODE_MAP = {
  80001: "当前IP地址不在允许范围内，请在指定IP下使用!",
  80002: "当前账号未在 ip 访问允许名单内，请联系管理员添加内部成员权限～"
} as const;

// 错误提示列表
export const BOKE_ERROR_MESSAGE_LIST = {
  ip: {
    message: "当前 ip 地址不在允许范围内，请在指定 ip 下使用！"
  },
  account: {
    message: "当前使用账号不在白名单范围内，请联系管理员添加权限!"
  }
};

/**
 * 纯函数：判断错误码是否为Boke错误码
 * 可以在任何上下文中安全使用，包括axios拦截器
 */
export const isBokeErrorCode = (errorCode: number): boolean => {
  return Object.keys(BOKE_ERROR_CODE_MAP).includes(errorCode.toString());
};

/**
 * 纯函数：获取Boke错误信息
 * 可以在任何上下文中安全使用
 */
export const getBokeErrorMessage = (errorCode: number): string => {
  return BOKE_ERROR_CODE_MAP[errorCode as keyof typeof BOKE_ERROR_CODE_MAP] || "无权限访问";
};

/**
 * 纯函数：显示访问受限弹窗
 * 使用已静态导入的store实例
 */
export const showBokeAccessDeniedModal = (errorCode: number): void => {
  if (!isBokeErrorCode(errorCode)) {
    return;
  }

  try {
    const accessDeniedStore = useAccessDeniedStore();

    // 如果已经显示过，则不重复显示
    if (accessDeniedStore.visible) {
      console.log("已经显示过，不重复显示");
      return;
    }

    const errorMessage = getBokeErrorMessage(errorCode);
    accessDeniedStore.showAccessDenied(errorMessage);
  } catch (error) {
    console.error("Failed to show access denied modal:", error);
  }
};

export function useBoke() {
  const router = useRouter();

  // 判断是否为波克用户登陆
  const isBokeUser = computed(() => {
    const userStore = useUserStore();
    const { userInfo } = storeToRefs(userStore);
    return userInfo.value.hasCompany;
  });

  // 使用导出的纯函数，保持向后兼容性
  const isBokeErrorCodeInternal = isBokeErrorCode;

  /**
   * 重定向到403
   */
  const redirectTo403 = (errorCode: number) => {
    const errorMessage = getBokeErrorMessage(errorCode);
    router.push({
      path: "/403",
      query: { message: errorMessage }
    });
  };

  /**
   * 根据错误码处理重定向
   */
  const handleErrorCodeRedirect = (errorCode: number) => {
    if (isBokeErrorCodeInternal(errorCode)) {
      redirectTo403(errorCode);
    }
  };

  /**
   * 显示访问受限制弹窗
   */
  const showAccessDeniedModal = (errorCode: number) => {
    if (isBokeErrorCodeInternal(errorCode)) {
      const accessDeniedStore = useAccessDeniedStore();
      console.log("显示访问受限制弹窗执行显示方法");
      // 如果已经显示过，则不重复显示
      if (accessDeniedStore.visible) {
        console.log("已经显示过，不重复显示");
        return;
      }
      // 提示文案
      const errorMessage = getBokeErrorMessage(errorCode);
      accessDeniedStore.showAccessDenied(errorMessage);
    }
  };

  return {
    redirectTo403,
    isBokeErrorCode: isBokeErrorCodeInternal,
    handleErrorCodeRedirect,
    showAccessDeniedModal,
    isBokeUser
  };
}
